// Shared validation schema used by both the API route (server-side)
// and the client-side form (via react-hook-form resolver pattern).

// Canonical slug values — used in DB, routing, and SEO dataset
export const SERVICE_TYPES = [
  'plumbing',
  'hvac',
  'electrical',
  'roofing',
  'landscaping',
  'pest-control',
  'house-cleaning',
  'moving-services',
  'locksmith',
  'water-damage-restoration',
  'foundation-repair',
  'painting-interior',
  'painting-exterior',
  'concrete-masonry',
  'tree-removal',
  'appliance-repair',
  'solar-panel-installation',
  'home-security',
  'garage-door-repair',
  'legal-services',
  'accounting-tax',
  'insurance-consulting',
] as const;

export type ServiceType = (typeof SERVICE_TYPES)[number];

// Human-readable labels for UI display only
export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  'plumbing': 'Plumbing',
  'hvac': 'HVAC',
  'electrical': 'Electrical',
  'roofing': 'Roofing',
  'landscaping': 'Landscaping',
  'pest-control': 'Pest Control',
  'house-cleaning': 'House Cleaning',
  'moving-services': 'Moving Services',
  'locksmith': 'Locksmith',
  'water-damage-restoration': 'Water Damage Restoration',
  'foundation-repair': 'Foundation Repair',
  'painting-interior': 'Painting (Interior)',
  'painting-exterior': 'Painting (Exterior)',
  'concrete-masonry': 'Concrete & Masonry',
  'tree-removal': 'Tree Removal',
  'appliance-repair': 'Appliance Repair',
  'solar-panel-installation': 'Solar Panel Installation',
  'home-security': 'Home Security',
  'garage-door-repair': 'Garage Door Repair',
  'legal-services': 'Legal Services',
  'accounting-tax': 'Accounting & Tax',
  'insurance-consulting': 'Insurance Consulting',
};

// Get label for a slug — falls back to the slug itself if not found
export function getServiceLabel(slug: string): string {
  return SERVICE_TYPE_LABELS[slug as ServiceType] ?? slug;
}

// Phone normalization — strips formatting to digits only for storage
export function normalizePhone(phone: string): string {
  return phone.replace(/\D/g, '');
}

// Phone display formatter — (XXX) XXX-XXXX
export function formatPhoneDisplay(digits: string): string {
  const d = digits.replace(/\D/g, '');
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  if (d.length === 11 && d[0] === '1') {
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return digits;
}

// ────────────────────────────────────────────────────────────
// Validation rules — used server-side in the API route
// Client-side uses react-hook-form with the same rule set
// ────────────────────────────────────────────────────────────

export interface LeadFormData {
  full_name: string;
  phone: string;
  email: string;
  city: string;
  service_type: string;
  problem_description: string;
  source_url?: string | null;
}

export interface ValidationError {
  field: keyof LeadFormData;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  data?: LeadFormData;
}

export function validateLeadSubmission(data: Partial<LeadFormData>): ValidationResult {
  const errors: ValidationError[] = [];

  // full_name
  if (!data.full_name || data.full_name.trim().length === 0) {
    errors.push({ field: 'full_name', message: 'Full name is required.' });
  } else if (data.full_name.trim().length < 2) {
    errors.push({ field: 'full_name', message: 'Please enter your full name (at least 2 characters).' });
  } else if (data.full_name.trim().length > 120) {
    errors.push({ field: 'full_name', message: 'Name must be under 120 characters.' });
  }

  // phone
  if (!data.phone || data.phone.trim().length === 0) {
    errors.push({ field: 'phone', message: 'Phone number is required.' });
  } else {
    const digits = normalizePhone(data.phone);
    if (digits.length < 10 || digits.length > 11) {
      errors.push({ field: 'phone', message: 'Enter a valid US phone number (10 digits).' });
    } else if (digits.length === 11 && digits[0] !== '1') {
      errors.push({ field: 'phone', message: 'Enter a valid US phone number.' });
    }
  }

  // email
  if (!data.email || data.email.trim().length === 0) {
    errors.push({ field: 'email', message: 'Email address is required.' });
  } else {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(data.email.trim())) {
      errors.push({ field: 'email', message: 'Enter a valid email address.' });
    } else if (data.email.trim().length > 254) {
      errors.push({ field: 'email', message: 'Email address is too long.' });
    }
  }

  // city
  if (!data.city || data.city.trim().length === 0) {
    errors.push({ field: 'city', message: 'City is required.' });
  } else if (data.city.trim().length < 2) {
    errors.push({ field: 'city', message: 'Enter a valid city name.' });
  } else if (data.city.trim().length > 100) {
    errors.push({ field: 'city', message: 'City name must be under 100 characters.' });
  }

  // service_type — accepts canonical slugs
  if (!data.service_type || data.service_type.trim().length === 0) {
    errors.push({ field: 'service_type', message: 'Please select the service type you need.' });
  } else if (!(SERVICE_TYPES as readonly string[]).includes(data.service_type.trim())) {
    errors.push({ field: 'service_type', message: 'Please select a valid service type from the list.' });
  }

  // problem_description
  if (!data.problem_description || data.problem_description.trim().length === 0) {
    errors.push({ field: 'problem_description', message: 'Please describe your problem or project.' });
  } else if (data.problem_description.trim().length < 20) {
    errors.push({ field: 'problem_description', message: "Please provide more detail (at least 20 characters). The more you share, the better quotes you'll receive." });
  } else if (data.problem_description.trim().length > 2000) {
    errors.push({ field: 'problem_description', message: 'Description must be under 2,000 characters.' });
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  return {
    valid: true,
    errors: [],
    data: {
      full_name: data.full_name!.trim(),
      phone: normalizePhone(data.phone!),
      email: data.email!.trim().toLowerCase(),
      city: data.city!.trim(),
      service_type: data.service_type!.trim(),
      problem_description: data.problem_description!.trim(),
      source_url: data.source_url?.trim() || null,
    } as LeadFormData,
  };
}